import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Droppable } from '../DndKit';

import './SamplesListContent.css';

import { useMtg } from '../../hook/useMtg';
import { useSample } from '../../hook/useSample';
import { cardFinish } from '../../hoc/MtgProvider';
import { buildClass } from '../../libs/classes';
import { ItemTypes } from '../../utils/items';
import { orderType } from '../SimpleComponents/OrderingSwitch';
import SampleRow from './SampleRow/SampleRow';
import SamplePackRow from './SamplePackRow/SamplePackRow';
import SampleTileDraggable from './SampleTile/SampleTileDraggable';

export const sortType = {
	price: 'price',
	rarity: 'rarity',
	dateChanged: 'date_changed',
	collectorNumber: 'collector_number',
	storage: 'storage',
	name: 'name',
};

/**
 * Content for SamplesList
 * @component
 */
const SamplesListContent = ({
	samples,
	isOwned,
	selectedIds,
	showStorage,
	show_checkbox,
	show_as_packs,
	show_as_tiles,
	sorting,
	ordering,
	onActivate,
	onSelect,
}) => {
	const { activeSample, updatedSample } = useSample();
	const { cutoffPrice } = useMtg();

	const [packs, setPacks] = useState(() => {
		if (show_as_packs && Object.keys(samples).length) {
			return getPacks(samples);
		}
		return {};
	});
	const [packs_unfold, setPacksUnfold] = useState([]);

	useEffect(() => {
		console.log('Initialization: SamplesListContent');
	}, []);

	useEffect(() => {
		if (show_as_packs) setPacks(getPacks(samples));
		else setPacks({});
	}, [samples, show_as_packs]);

	const sortSamples = (id1, id2) => {
		const s1 = samples[id1];
		const s2 = samples[id2];

		let result = 0;
		switch (sorting) {
			case sortType.price: {
				let p1 = 0;
				if (s1.finish === cardFinish.nonfoil) {
					p1 = s1.card.price_usd;
				} else if (s1.finish === cardFinish.foil) {
					p1 = s1.card.price_usd_foil;
				} else if (s1.finish === cardFinish.etched) {
					p1 = s1.card.price_usd_etched;
				}
				let p2 = 0;
				if (s2.finish === cardFinish.nonfoil) {
					p2 = s2.card.price_usd;
				} else if (s2.finish === cardFinish.foil) {
					p2 = s2.card.price_usd_foil;
				} else if (s2.finish === cardFinish.etched) {
					p2 = s2.card.price_usd_etched;
				}
				result = p1 - p2;
				break;
			}
			case sortType.rarity: {
				result = s1.card.rarity - s2.card.rarity;
				break;
			}
			case sortType.dateChanged:
				result = Date.parse(s1.date_changed) - Date.parse(s2.date_changed);
				break;
			case sortType.storage:
				result = s2.storage_id - s1.storage_id;
				break;
			case sortType.name:
				result = s1.card.name > s2.card.name ? 1 : -1;
				break;
			case sortType.collectorNumber:
			default:
				if (s1.card.set_id !== s2.card.set_id) result = s2.card.set_id - s1.card.set_id;
				else result = parseInt(s1.card.c_number) > parseInt(s2.card.c_number) ? 1 : -1;
		}
		return ordering === orderType.ascending ? result : -result;
	};

	const sortPacks = (id1, id2) => {
		const p1 = packs[id1];
		const p2 = packs[id2];
		const s1 = p1.samples[0];
		const s2 = p2.samples[0];
		let result = 0;
		switch (sorting) {
			case sortType.price: {
				let p1 = 0;
				if (s1.finish === cardFinish.nonfoil) {
					p1 = s1.card.price_usd;
				} else if (s1.finish === cardFinish.foil) {
					p1 = s1.card.price_usd_foil;
				} else if (s1.finish === cardFinish.etched) {
					p1 = s1.card.price_usd_etched;
				}
				let p2 = 0;
				if (s2.finish === cardFinish.nonfoil) {
					p2 = s2.card.price_usd;
				} else if (s2.finish === cardFinish.foil) {
					p2 = s2.card.price_usd_foil;
				} else if (s2.finish === cardFinish.etched) {
					p2 = s2.card.price_usd_etched;
				}
				result = p1 - p2;
				break;
			}
			case sortType.rarity: {
				result = s1.card.rarity - s2.card.rarity;
				break;
			}
			case sortType.dateChanged:
				result = Date.parse(s1.date_changed) - Date.parse(s2.date_changed);
				break;
			case sortType.name:
				result = s1.card.name > s2.card.name ? 1 : -1;
				break;
			case sortType.collectorNumber:
			default:
				if (s1?.card?.set_id !== s2?.card?.set_id) result = s2?.card?.set_id - s1?.card?.set_id;
				else result = parseInt(s1?.card?.c_number) > parseInt(s2?.card?.c_number) ? 1 : -1;
		}
		return ordering === orderType.ascending ? result : -result;
	};

	const renderPacksList = () => {
		return (
			<div className="content scrollable">
				{!Object.keys(packs).length ? <div className="no-samples">So far, it&apos;s empty</div> : null}
				{Object.keys(packs)
					.sort((id1, id2) => sortPacks(id1, id2))
					.map((id) => renderRow(id))}
			</div>
		);
	};

	const renderSamplesList = () => {
		return (
			<div className="content scrollable">
				{!Object.keys(samples).length ? <div className="no-samples">So far, it&apos;s empty</div> : null}
				{Object.keys(samples)
					.sort((id1, id2) => sortSamples(id1, id2))
					.map((id) => renderSampleRow(samples[id]))}
			</div>
		);
	};

	const renderTilesList = () => {
		return (
			<div className="content scrollable">
				{!Object.keys(samples).length ? <div className="no-samples">So far, it&apos;s empty</div> : null}
				{Object.keys(samples)
					.sort((id1, id2) => sortSamples(id1, id2))
					.map((id, index) => renderSampleTile(samples[id], index))}
			</div>
		);
	};

	const renderSampleRow = (sample) => {
		return (
			<SampleRow
				activeSampleId={activeSample ? activeSample.id : 0}
				cutoffPrice={cutoffPrice}
				isOwned={isOwned}
				isSelected={selectedIds.has(sample.id)}
				key={sample.id}
				sample={sample}
				show_checkbox={show_checkbox}
				showStorage={showStorage}
				updSampleId={updatedSample ? updatedSample.id : 0}
				onClick={onActivate}
				onSelect={onSelect}
			/>
		);
	};

	const renderRow = (id) => {
		const pack = packs[id];
		// Pack row
		if (pack.samples.length > 1) {
			return (
				<SamplePackRow
					isOwned={isOwned}
					key={'pack_' + pack.card.id}
					pack={pack}
					showStorage={showStorage}
					unfold={packs_unfold.indexOf(pack.card.id) !== -1 ? true : false}
					onClick={handlePackToggle}
				>
					{Object.keys(pack.samples).map((sample_id) => renderSampleRow(pack.samples[sample_id]))}
				</SamplePackRow>
			);
		}
		// Sample row
		if (pack.samples.length === 1) {
			return renderSampleRow(pack.samples[0]);
		}
	};

	const renderSampleTile = (sample, index) => {
		return (
			<SampleTileDraggable
				activeSampleId={activeSample ? activeSample.id : 0}
				cutoffPrice={cutoffPrice}
				index={index}
				key={sample.id}
				sample={sample}
				type={ItemTypes.SAMPLE}
				updSampleId={updatedSample ? updatedSample.id : 0}
				onClick={onActivate}
			/>
		);
	};

	// FIXME: Droppable requires drag'n'drop context, which not implemented for all instances of SimpleList (e.g AllCardsPage)
	return (
		<div className="sample-list-content content scrollable">
			<Droppable
				droppableId="collection"
				isDropDisabled={false}
				type="TILE"
				//mode="virtual"
			>
				{(provided, snapshot) => (
					<div
						className={buildClass('storage-droppable').add(snapshot.isDraggingOver, 'dragged-over')}
						ref={provided.innerRef}
						{...provided.droppableProps}
					>
						{(show_as_tiles && <div className="tiles-list">{renderTilesList()}</div>) ||
							(show_as_packs && <div className="packs-list">{renderPacksList()}</div>) || (
								<div className="samples-list">{renderSamplesList()}</div>
							)}
						<div>{provided.placeholder}</div>
					</div>
				)}
			</Droppable>
		</div>
	);

	// Prepare sample packs
	function getPacks(samples) {
		let packsRes = {};
		for (let id in samples) {
			let sample = samples[id];
			if (!(sample.card_id in packsRes)) {
				// Create a new pack and add to it
				packsRes[sample.card_id] = {
					card: sample.card,
					set: sample.set,
					samples: [sample],
					count: sample.qty,
					storage_id: sample.storage_id,
				};
			} else {
				// Add to existing pack
				packsRes[sample.card_id].samples.push(sample);
				packsRes[sample.card_id].count += sample.qty;
			}
		}
		return packsRes;
	}

	function handlePackToggle(card_id) {
		let packsUnfoldUpd = [...packs_unfold];
		if (packsUnfoldUpd.indexOf(card_id) !== -1) {
			const index = packsUnfoldUpd.indexOf(card_id);
			packsUnfoldUpd.splice(index, 1);

			setPacksUnfold(packsUnfoldUpd);

			// Remove active if active sample inside pack
			if (activeSample && activeSample.card.id === card_id) {
				onActivate(null);
			}
		} else {
			packsUnfoldUpd.push(card_id);

			setPacksUnfold(packsUnfoldUpd);
			if (card_id in packs) {
				const pack = packs[card_id];
				if (pack.samples.length) {
					onActivate(pack.samples[0]);
				}
			}
		}
	}
};

SamplesListContent.propTypes = {
	// Samples list
	samples: PropTypes.objectOf(PropTypes.object).isRequired,
	// Is this list owned by current user
	isOwned: PropTypes.bool.isRequired,
	// Set of selected samples IDs
	selectedIds: PropTypes.instanceOf(Set),
	// Flag that storage should be displayed for each sample
	showStorage: PropTypes.bool,
	// Flag that checkbox should be displayed for each sample
	show_checkbox: PropTypes.bool,
	// Flag that similar samples should be combined into packs
	show_as_packs: PropTypes.bool,
	// Flag that cards shoul be represented as tiles
	show_as_tiles: PropTypes.bool,
	// Selected sorting
	sorting: PropTypes.string,
	// Direction of selected sort
	ordering: PropTypes.number,
	// Callback on activate (select as active) sample
	onActivate: PropTypes.func.isRequired,
	// Callback on select (with checkbox) sample
	onSelect: PropTypes.func,
};

export default SamplesListContent;
